import React, { useState, useEffect } from "react";
import { useApp } from "../../contexts/AppContext";
import { executionService } from "../../services/executionService";

export const AdminSignatureManager = () => {
  const { service } = useApp();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [assignedSignatures, setAssignedSignatures] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // success, error
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [tableFilter, setTableFilter] = useState("");
  const [inputMode, setInputMode] = useState("upload"); // "upload" or "canvas"
  const canvasRef = React.useRef(null);
  const previewCanvasRef = React.useRef(null);
  const [isDrawing, setIsDrawing] = React.useState(false);

  // Live Active Directory search for the approver email field — separate
  // from the table filter below, which was previously (incorrectly) sharing
  // the same state, and separate from the old "only people who've already
  // been an approver" list, which blocked assigning a signature to anyone new.
  const [emailSearchQuery, setEmailSearchQuery] = useState("");
  const [emailSearchResults, setEmailSearchResults] = useState([]);
  const [searchingEmails, setSearchingEmails] = useState(false);

  useEffect(() => {
    loadAssignedSignatures();
  }, []);

  useEffect(() => {
    if (!emailSearchQuery.trim()) {
      setEmailSearchResults([]);
      return;
    }
    const handle = setTimeout(async () => {
      setSearchingEmails(true);
      try {
        const results = await service.searchUsers(emailSearchQuery);
        const users = (results?.value || []).map(u => ({
          displayName: u.displayName,
          mail: u.mail,
        })).filter(u => u.mail);
        setEmailSearchResults(users);
      } catch (error) {
        console.error('[AdminSignatureManager] Error searching users:', error);
        setEmailSearchResults([]);
      } finally {
        setSearchingEmails(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [emailSearchQuery, service]);

  const loadAssignedSignatures = async () => {
    setLoading(true);
    try {
      // Get all signatures from ApproverSignatures
      const signatures = await service.getListItems("ApproverSignatures");
      const assigned = (signatures?.value || []).map(s => ({
        id: s.id,
        email: s.fields?.ApproverEmail,
        fileName: s.fields?.SignatureFileName,
        signatureData: s.fields?.SignatureData,
        createdAt: s.fields?.CreatedAt
      }));

      // Deduplicate: keep only the latest signature per email
      const dedupedMap = new Map();
      assigned.forEach(sig => {
        const existing = dedupedMap.get(sig.email);
        if (!existing || (sig.createdAt && existing.createdAt && new Date(sig.createdAt) > new Date(existing.createdAt))) {
          dedupedMap.set(sig.email, sig);
        }
      });

      const deduped = Array.from(dedupedMap.values());
      setAssignedSignatures(deduped);
      console.log('[AdminSignatureManager] Loaded', deduped.length, 'assigned signatures (deduplicated from', assigned.length, ')');
    } catch (error) {
      console.error('[AdminSignatureManager] Error loading signatures:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setMessage("Please select an image file (PNG, JPG, etc.)");
      setMessageType("error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setMessage("File size must be less than 5MB");
      setMessageType("error");
      return;
    }

    setSelectedFile(file);
    setMessage("");
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX !== undefined ? e.clientX : e.touches?.[0]?.clientX;
    const clientY = e.clientY !== undefined ? e.clientY : e.touches?.[0]?.clientY;
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const syncToPreview = () => {
    const canvas = canvasRef.current;
    const previewCanvas = previewCanvasRef.current;
    if (!canvas || !previewCanvas) return;

    const ctx = previewCanvas.getContext("2d");
    ctx.drawImage(canvas, 0, 0);
  };

  const draw = (e) => {
    if (!isDrawing) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX !== undefined ? e.clientX : e.touches?.[0]?.clientX;
    const clientY = e.clientY !== undefined ? e.clientY : e.touches?.[0]?.clientY;
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#000";
    ctx.lineTo(x, y);
    ctx.stroke();

    syncToPreview();
  };

  const stopDrawing = () => {
    console.log('[CANVAS] stopDrawing called');
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    const previewCanvas = previewCanvasRef.current;
    if (previewCanvas) {
      const previewCtx = previewCanvas.getContext("2d");
      previewCtx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
    }
  };

  const handleAssignSignature = async () => {
    if (!selectedEmail.trim()) {
      setMessage("Please select an approver email");
      setMessageType("error");
      return;
    }

    let base64 = null;

    if (inputMode === "upload") {
      if (!selectedFile) {
        setMessage("Please select a signature image");
        setMessageType("error");
        return;
      }

      setUploading(true);
      setMessage("");

      try {
        const reader = new FileReader();
        reader.onload = async (event) => {
          base64 = event.target?.result;
          await saveSignature(base64);
        };
        reader.readAsDataURL(selectedFile);
      } finally {
        setUploading(false);
      }
    } else {
      // Canvas mode
      const canvas = canvasRef.current;
      if (!canvas) {
        setMessage("Canvas not available");
        setMessageType("error");
        return;
      }

      base64 = canvas.toDataURL("image/png");
      if (base64 === "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==") {
        setMessage("Please sign on the canvas before saving");
        setMessageType("error");
        return;
      }

      setUploading(true);
      await saveSignature(base64);
    }
  };

  const saveSignature = async (base64) => {
    try {
      const existing = assignedSignatures.find(s => s.email === selectedEmail);

      if (existing) {
        await service.updateListItem("ApproverSignatures", existing.id, {
          Title: `${selectedEmail}`,
          ApproverEmail: selectedEmail,
          SignatureFileName: `${selectedEmail}_${Date.now()}.png`,
          SignatureData: base64,
          CreatedAt: new Date().toISOString()
        });
        setMessage(`✓ Signature updated for ${selectedEmail}`);
      } else {
        await service.createListItem("ApproverSignatures", {
          Title: `${selectedEmail}`,
          ApproverEmail: selectedEmail,
          SignatureFileName: `${selectedEmail}_${Date.now()}.png`,
          SignatureData: base64,
          CreatedAt: new Date().toISOString()
        });
        setMessage(`✓ Signature assigned to ${selectedEmail}`);
      }

      setMessageType("success");
      setSelectedFile(null);
      setSelectedEmail("");
      setInputMode("upload");
      clearCanvas();
      // #file-input only exists in the DOM in upload mode — it's not
      // rendered at all in canvas mode, so this must be null-checked.
      const fileInput = document.getElementById("file-input");
      if (fileInput) fileInput.value = "";
      loadAssignedSignatures();
    } catch (error) {
      setMessage("Failed to save signature: " + error.message);
      setMessageType("error");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteSignature = async (signatureId, email) => {
    try {
      await service.deleteListItem("ApproverSignatures", signatureId);
      setMessage(`✓ Signature removed from ${email}`);
      setMessageType("success");
      setDeleteConfirm(null);
      loadAssignedSignatures();
    } catch (error) {
      setMessage("Failed to delete signature: " + error.message);
      setMessageType("error");
    }
  };

  const filteredAssignedSignatures = assignedSignatures.filter(sig =>
    sig.email?.toLowerCase().includes(tableFilter.toLowerCase())
  );

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="font-serif text-3xl font-medium text-gray-900 m-0 mb-1">
          Approver Signature Management
        </h1>
        <p className="text-gray-700 m-0">Upload and assign signatures to approvers. Each approver can have one signature.</p>
        <div className="h-0.5 bg-gradient-to-r from-blue-600 to-transparent mt-2" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Upload Section */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Add Signature</h2>

          {/* Tabs */}
          <div className="flex gap-2 mb-4 border-b border-gray-200">
            <button
              onClick={() => setInputMode("upload")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                inputMode === "upload"
                  ? "text-blue-600 border-blue-600"
                  : "text-gray-700 border-transparent hover:text-gray-700"
              }`}
            >
              📤 Upload Image
            </button>
            <button
              onClick={() => setInputMode("canvas")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                inputMode === "canvas"
                  ? "text-blue-600 border-blue-600"
                  : "text-gray-700 border-transparent hover:text-gray-700"
              }`}
            >
              ✏️ Sign on Canvas
            </button>
          </div>

          <div className="space-y-4">
            {/* File Upload */}
            {inputMode === "upload" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Signature Image *</label>
              <label className="block border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-600 transition-colors">
                <div className="text-3xl mb-2">📷</div>
                <p className="text-gray-700 font-medium">Click to upload signature</p>
                <p className="text-xs text-gray-700 mt-1">PNG, JPG (max 5MB)</p>
                <input
                  id="file-input"
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  disabled={uploading}
                  className="hidden"
                />
              </label>
              {selectedFile && (
                <div className="mt-2 p-3 bg-gray-100 rounded text-sm text-gray-800">
                  ✓ Selected: {selectedFile.name}
                </div>
              )}
            </div>
            )}

            {/* Canvas Drawing */}
            {inputMode === "canvas" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Draw Signature *</label>
              <p className="text-sm text-gray-700 mb-3">Click and drag to draw your signature.</p>
              <div className="flex justify-center">
                <canvas
                  ref={canvasRef}
                  width={400}
                  height={120}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  className="border-2 border-gray-300 rounded-lg bg-white cursor-crosshair touch-none"
                  style={{ touchAction: "none", display: "block" }}
                />
              </div>
              <button
                onClick={clearCanvas}
                className="mt-2 w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-lg font-medium"
              >
                Clear
              </button>
            </div>
            )}

            {/* Email Selection — searches Active Directory live */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Approver Email *</label>
              <input
                type="text"
                placeholder="Search by name or email..."
                value={emailSearchQuery}
                onChange={(e) => setEmailSearchQuery(e.target.value)}
                autoComplete="off"
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 mb-2"
              />
              {emailSearchQuery && (
                <div className="border border-gray-300 rounded-lg bg-gray-100 max-h-48 overflow-y-auto mb-2">
                  {searchingEmails ? (
                    <div className="px-3 py-2 text-gray-700 text-sm">Searching...</div>
                  ) : emailSearchResults.length > 0 ? (
                    emailSearchResults.map(user => (
                      <button
                        key={user.mail}
                        onClick={() => {
                          setSelectedEmail(user.mail);
                          setEmailSearchQuery("");
                          setEmailSearchResults([]);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-gray-200 text-gray-800 text-sm"
                      >
                        <div className="font-medium">{user.displayName}</div>
                        <div className="text-xs text-gray-700">{user.mail}</div>
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-2">
                      <div className="text-gray-700 text-sm mb-1.5">No directory match.</div>
                      {emailSearchQuery.includes("@") && (
                        <button
                          onClick={() => {
                            setSelectedEmail(emailSearchQuery.trim());
                            setEmailSearchQuery("");
                            setEmailSearchResults([]);
                          }}
                          className="w-full text-left px-2 py-1.5 bg-gray-200 hover:bg-gray-300 rounded text-blue-400 text-sm"
                        >
                          ✏️ Use "{emailSearchQuery.trim()}" anyway
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
              {selectedEmail && (
                <div className="p-3 bg-blue-50 border border-blue-300 rounded text-sm text-blue-800 mb-2 flex items-center justify-between gap-2">
                  <span>✓ Selected: {selectedEmail}</span>
                  <button onClick={() => setSelectedEmail("")} className="text-blue-700 hover:text-blue-800 text-xs underline flex-shrink-0">Change</button>
                </div>
              )}
            </div>

            {/* Message */}
            {message && (
              <div className={`p-3 rounded text-sm ${
                messageType === "success"
                  ? "bg-green-50 text-green-700 border border-green-300"
                  : "bg-red-50 text-red-700 border border-red-300"
              }`}>
                {message}
              </div>
            )}

            {/* Assign Button */}
            <button
              onClick={handleAssignSignature}
              disabled={uploading || !selectedEmail || (inputMode === "upload" && !selectedFile)}
              className="w-full px-4 py-3 bg-blue-700 hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
            >
              {uploading ? "Assigning..." : "✓ Assign Signature"}
            </button>
          </div>
        </div>

        {/* Preview Section */}
        {(selectedFile || inputMode === "canvas") && (
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Preview</h2>
            <div className="bg-gray-100 rounded p-4 flex items-center justify-center min-h-40">
              {inputMode === "upload" && selectedFile ? (
                <img
                  src={URL.createObjectURL(selectedFile)}
                  alt="Signature preview"
                  className="max-h-40 object-contain"
                />
              ) : inputMode === "canvas" ? (
                <canvas
                  ref={previewCanvasRef}
                  width={300}
                  height={100}
                  className="bg-white rounded"
                  style={{ display: "block" }}
                />
              ) : null}
            </div>
            <p className="text-xs text-gray-700 mt-3 text-center">
              Will be assigned to: {selectedEmail || "(select email)"}
            </p>
          </div>
        )}
      </div>

      {/* Assigned Signatures List */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-blue-50 px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">Assigned Signatures</h2>
          <p className="text-xs text-gray-700 mt-1">{assignedSignatures.length} approver(s) have signatures assigned</p>
        </div>

        {loading ? (
          <div className="p-12 text-center text-gray-700">Loading...</div>
        ) : filteredAssignedSignatures.length === 0 ? (
          <div className="p-12 text-center text-gray-700">
            {tableFilter ? "No signatures match your search" : "No signatures assigned yet"}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100 border-b border-gray-300">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Signature</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Assigned Date</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssignedSignatures.map(sig => (
                  <tr key={sig.id} className="border-b border-gray-200 hover:bg-gray-100/50">
                    <td className="px-6 py-4 text-sm text-gray-800 font-medium">{sig.email}</td>
                    <td className="px-6 py-4">
                      {sig.signatureData && (
                        <img
                          src={sig.signatureData}
                          alt="Signature"
                          className="h-8 object-contain"
                        />
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {sig.createdAt ? new Date(sig.createdAt).toLocaleDateString() : "-"}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => setDeleteConfirm(sig.id)}
                        className="px-3 py-1.5 bg-red-50 hover:bg-red-50 text-red-700 rounded text-xs font-medium transition-colors"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Search filter for signatures */}
      {assignedSignatures.length > 0 && (
        <div className="mt-4 bg-white border border-gray-200 rounded-xl p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Filter by email</label>
          <input
            type="text"
            placeholder="Search assigned signatures..."
            value={tableFilter}
            onChange={(e) => setTableFilter(e.target.value)}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500"
          />
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-white border border-red-300 rounded-xl p-6 max-w-sm text-center">
            <h3 className="text-lg font-semibold text-red-700 mb-4">Remove Signature?</h3>
            <p className="text-gray-700 mb-6">This approver will no longer have an assigned signature.</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const sig = assignedSignatures.find(s => s.id === deleteConfirm);
                  handleDeleteSignature(deleteConfirm, sig?.email);
                }}
                className="px-4 py-2 bg-red-700 hover:bg-red-800 text-white rounded-lg font-medium"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
